import { cx } from '@vizit/theme';
import * as React from 'react';
import {
  ToggleButton as AriaToggleButton,
  ToggleButtonGroup as AriaToggleButtonGroup,
  type ToggleButtonGroupProps as AriaToggleButtonGroupProps,
  type ToggleButtonProps as AriaToggleButtonProps,
  type Key,
} from 'react-aria-components';
import { composeTailwindRenderProps } from '../../utils';
import { tabClasses, tabListClasses } from '../tabs/styles';
import { TabSize, TabVariant } from '../tabs/types';

/** The group's size, shared with its items so they always match. */
const ToggleButtonGroupSizeContext = React.createContext<TabSize>(TabSize.Md);

/** Square item sizes for icon-only items, matching the height of each tab size. */
const ICON_ONLY_CLASSES: Record<TabSize, string> = {
  [TabSize.Sm]: 'w-14 px-0',
  [TabSize.Md]: 'w-16 px-0',
  [TabSize.Lg]: 'w-18 px-0',
};

/**
 * Exactly one item is always selected, so the group needs a starting selection: pass `selectedKey` (controlled) or
 * `defaultSelectedKey` (uncontrolled).
 */
type ToggleButtonGroupSelection =
  | {
      /** The selected item's `id` (controlled). */
      selectedKey: Key;
      defaultSelectedKey?: never;
    }
  | {
      /** The initially selected item's `id` (uncontrolled). */
      defaultSelectedKey: Key;
      selectedKey?: never;
    };

interface ToggleButtonGroupBaseProps
  extends Omit<
    AriaToggleButtonGroupProps,
    'selectionMode' | 'selectedKeys' | 'defaultSelectedKeys' | 'onSelectionChange' | 'disallowEmptySelection'
  > {
  /**
   * Called with the newly selected item's `id`. One item is always selected, so it never receives nothing.
   *
   * @optional
   */
  onSelectionChange?: (key: Key) => void;
  /**
   * The size of the items, on the Tabs size scale.
   *
   * @optional
   * @default 'md'
   */
  size?: TabSize;
}

type ToggleButtonGroupProps = ToggleButtonGroupBaseProps & ToggleButtonGroupSelection;

/**
 * A segmented control: a row of buttons where exactly one is selected, such as a view or grid-size switch. It looks
 * like the default Tabs (a gray well with the selected item raised on white) but behaves as a toggle group, since it
 * changes how one view is shown instead of swapping panels.
 *
 * @example
 * <ToggleButtonGroup aria-label="Grid size" selectedKey={gridSize} onSelectionChange={setGridSize}>
 *   <ToggleButtonGroup.Item id="small" aria-label="Small grid" icon={<GridSmallIcon size={20} />} />
 *   <ToggleButtonGroup.Item id="single" aria-label="Single image" icon={<GridSingleIcon size={20} />} />
 * </ToggleButtonGroup>
 */
function ToggleButtonGroup(props: ToggleButtonGroupProps) {
  const {
    selectedKey,
    defaultSelectedKey,
    onSelectionChange,
    size = TabSize.Md,
    className,
    children,
    ...other
  } = props;

  const isControlled = selectedKey !== undefined;
  // Uncontrolled only: the selection React Aria holds internally. A controlled group reads `selectedKey` instead, so a
  // change the parent declines can be requested again.
  const uncontrolledKeyRef = React.useRef(defaultSelectedKey);

  return (
    <ToggleButtonGroupSizeContext value={size}>
      <AriaToggleButtonGroup
        {...other}
        selectionMode="single"
        disallowEmptySelection
        selectedKeys={selectedKey === undefined ? undefined : [selectedKey]}
        defaultSelectedKeys={defaultSelectedKey === undefined ? undefined : [defaultSelectedKey]}
        onSelectionChange={(keys) => {
          const [key] = keys;
          const currentKey = isControlled ? selectedKey : uncontrolledKeyRef.current;
          // React Aria reports pressing the already-selected item as a change; only pass on real ones
          if (key === undefined || key === currentKey) {
            return;
          }
          if (!isControlled) {
            uncontrolledKeyRef.current = key;
          }
          onSelectionChange?.(key);
        }}
        className={composeTailwindRenderProps(className, tabListClasses({ variant: TabVariant.Default }))}
      >
        {children}
      </AriaToggleButtonGroup>
    </ToggleButtonGroupSizeContext>
  );
}

interface ToggleButtonGroupItemProps extends Omit<AriaToggleButtonProps, 'children' | 'id'> {
  /**
   * The item's key, reported by the group's selection.
   *
   * @required
   */
  id: Key;
  /**
   * The icon, shown before the label. With no label the item is a square icon button and needs an `aria-label`.
   *
   * @optional
   */
  icon?: React.ReactNode;
  /**
   * The text label.
   *
   * @optional
   */
  children?: React.ReactNode;
}

function ToggleButtonGroupItem(props: ToggleButtonGroupItemProps) {
  const { icon, children, className, ...other } = props;
  const size = React.useContext(ToggleButtonGroupSizeContext);
  const isIconOnly = Boolean(icon) && !children;

  return (
    <AriaToggleButton
      {...other}
      className={composeTailwindRenderProps(
        className,
        cx(tabClasses({ variant: TabVariant.Default, size }), isIconOnly && ICON_ONLY_CLASSES[size]),
      )}
    >
      {icon}
      {children}
    </AriaToggleButton>
  );
}

const _ToggleButtonGroup = Object.assign(ToggleButtonGroup, { Item: ToggleButtonGroupItem });

export { _ToggleButtonGroup as ToggleButtonGroup };
export type { ToggleButtonGroupItemProps, ToggleButtonGroupProps };
